import React, { useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext";

type Row = {
  codigo: string;
  nomeProduto: string;
  quantidadeEstoque: number;
  quantidadeSelecionada: number;
};

type TableProps = {
  headers: (keyof Row)[];
  rows: Row[];
  onUpdateRows: (updated: Row[]) => void;
  columnWidths?: string[];
  compact?: boolean;
};

const MovementTable: React.FC<TableProps> = ({
  headers,
  rows,
  onUpdateRows,
  columnWidths = [],
  compact = true,
}) => {
  const [filters, setFilters] = useState<string[]>(
    Array(headers.length).fill("")
  );
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [hoverRow, setHoverRow] = useState<number | null>(null);

  const { departmentColor } = useTheme();

  const borderColor = "#d9dde3";
  const headerBg = "#f1f2f4";
  const headerText = "#454545";
  const filterBg = "#fafafa";
  const footerBg = "#f6f7f9";
  const footerBorder = "#d5d9df";

  const baseSelectionColor = departmentColor || "#0a343d";
  const optionSelected = `${baseSelectionColor}C0`;
  const optionHover = `${baseSelectionColor}22`;

  const zebraOdd = "#fbfbfc";
  const zebraEven = "#ffffff";

  const headerFontSize = compact ? "12px" : "14px";
  const filterFontSize = compact ? "11.5px" : "13px";
  const cellFontSize = compact ? "12px" : "14px";
  const qtyFontSize = compact ? "12.5px" : "15px";
  const footerFontSize = compact ? "11px" : "12px";

  const headerHeight = compact ? "30px" : "36px";
  const filterHeight = compact ? "28px" : "32px";
  const cellHeight = compact ? "30px" : "36px";

  const inputHeight = compact ? "22px" : "26px";
  const qtyButtonSize = compact ? 22 : 26;
  const qtyButtonFontSize = compact ? 14 : 16;

  const getHeaderLabel = (header: keyof Row) => {
    if (header === "codigo") return "Código do Produto";
    if (header === "nomeProduto") return "Nome do Produto";
    if (header === "quantidadeEstoque") return "Q. Atual";
    if (header === "quantidadeSelecionada") return "Q. Selecionada";
    return String(header);
  };

  const getColumnStyle = (index: number): React.CSSProperties => ({
    width: columnWidths[index] || "auto",
    minWidth: columnWidths[index] || "auto",
    maxWidth: columnWidths[index] || "auto",
  });

  const rowBaseBg = (index: number) => (index % 2 === 0 ? zebraOdd : zebraEven);

  const handleFilterChange = (index: number, value: string) => {
    const next = [...filters];
    next[index] = value.toUpperCase();
    setFilters(next);
    setSelectedRow(null);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      headers.every((key, i) => {
        const cellValue = String(row[key] ?? "").toUpperCase();
        return cellValue.includes(filters[i]);
      })
    );
  }, [rows, headers, filters]);

  const handleQuantityChange = (
    filteredIndex: number,
    operation: "+" | "-"
  ) => {
    const targetRow = filteredRows[filteredIndex];
    if (!targetRow) return;

    const originalIndex = rows.findIndex(
      (r) =>
        r.codigo === targetRow.codigo &&
        r.nomeProduto === targetRow.nomeProduto &&
        r.quantidadeEstoque === targetRow.quantidadeEstoque &&
        r.quantidadeSelecionada === targetRow.quantidadeSelecionada
    );

    if (originalIndex < 0) return;

    const newRows = [...rows];
    const currentQuantity = newRows[originalIndex].quantidadeSelecionada;

    if (
      operation === "+" &&
      currentQuantity < newRows[originalIndex].quantidadeEstoque
    ) {
      newRows[originalIndex].quantidadeSelecionada = currentQuantity + 1;
    } else if (operation === "-" && currentQuantity > 0) {
      newRows[originalIndex].quantidadeSelecionada = currentQuantity - 1;
    }

    if (newRows[originalIndex].quantidadeSelecionada === 0) {
      newRows.splice(originalIndex, 1);
    }

    onUpdateRows(newRows);
  };

  return (
    <div style={{ ...styles.wrapper, marginTop: compact ? 18 : 28 }}>
      <div style={styles.scroller}>
        <table
          style={{
            ...styles.table,
            fontFamily: "'Open Sans', sans-serif",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={String(header)}
                  style={{
                    ...styles.th,
                    ...getColumnStyle(index),
                    backgroundColor: headerBg,
                    color: headerText,
                    fontSize: headerFontSize,
                    height: headerHeight,
                    borderColor,
                  }}
                >
                  <div
                    style={{
                      ...styles.thInner,
                      padding: compact ? "0 7px" : "0 10px",
                      gap: compact ? 5 : 8,
                    }}
                  >
                    <span style={styles.thText}>{getHeaderLabel(header)}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              {headers.map((header, index) => (
                <td
                  key={`filter-${String(header)}`}
                  style={{
                    ...styles.filterCell,
                    ...getColumnStyle(index),
                    height: filterHeight,
                    borderColor,
                    background: filterBg,
                    padding: compact ? "0 5px" : "0 8px",
                  }}
                >
                  <input
                    type="text"
                    value={filters[index]}
                    onChange={(e) =>
                      handleFilterChange(index, e.target.value.toUpperCase())
                    }
                    style={{
                      ...styles.filterInput,
                      fontSize: filterFontSize,
                      height: inputHeight,
                    }}
                  />
                </td>
              ))}
            </tr>

            {filteredRows.map((linha, rowIndex) => {
              const isSelected = selectedRow === rowIndex;
              const isHovered = hoverRow === rowIndex;

              let rowBg = rowBaseBg(rowIndex);
              if (isHovered && !isSelected) rowBg = optionHover;
              if (isSelected) rowBg = optionSelected;

              return (
                <tr
                  key={`${linha.codigo}-${rowIndex}`}
                  data-row="true"
                  onClick={() => setSelectedRow(rowIndex)}
                  onMouseEnter={() => setHoverRow(rowIndex)}
                  onMouseLeave={() => setHoverRow(null)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: rowBg,
                    transition: "background-color 0.14s ease",
                  }}
                >
                  {headers.map((header, colIndex) => (
                    <td
                      key={`${String(header)}-${colIndex}`}
                      style={{
                        ...styles.td,
                        ...getColumnStyle(colIndex),
                        height: cellHeight,
                        borderColor,
                        fontSize: cellFontSize,
                        padding: compact ? "0 7px" : "0 10px",
                      }}
                    >
                      {header === "quantidadeSelecionada" ? (
                        <div
                          style={{
                            ...styles.qtyWrap,
                            gap: compact ? 7 : 10,
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(rowIndex, "-");
                            }}
                            style={{
                              ...styles.qtyBtn,
                              width: qtyButtonSize,
                              height: qtyButtonSize,
                              fontSize: qtyButtonFontSize,
                              color: "#5f6775",
                              borderColor,
                              background: "#ffffff",
                            }}
                          >
                            −
                          </button>

                          <span
                            style={{
                              ...styles.qtyValue,
                              fontSize: qtyFontSize,
                              minWidth: compact ? 16 : 22,
                            }}
                          >
                            {linha.quantidadeSelecionada}
                          </span>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(rowIndex, "+");
                            }}
                            style={{
                              ...styles.qtyBtn,
                              width: qtyButtonSize,
                              height: qtyButtonSize,
                              fontSize: qtyButtonFontSize,
                              color: "#ffffff",
                              borderColor: baseSelectionColor,
                              background: baseSelectionColor,
                            }}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            ...styles.cellInner,
                            fontSize: cellFontSize,
                          }}
                        >
                          {linha[header]}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}

            {!filteredRows.length && (
              <tr>
                <td
                  colSpan={headers.length}
                  style={{
                    ...styles.emptyCell,
                    fontSize: cellFontSize,
                    borderColor,
                    padding: compact ? 12 : 18,
                  }}
                >
                  Nenhum registro encontrado com os filtros atuais.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          ...styles.bottomBar,
          height: compact ? 30 : 38,
          padding: compact ? "0 8px" : "0 10px",
          background: footerBg,
          borderTop: `1px solid ${footerBorder}`,
        }}
      >
        <div
          style={{
            ...styles.bottomLeft,
            fontSize: footerFontSize,
          }}
        >
          1 - {filteredRows.length} de {filteredRows.length} registros
        </div>

        <div
          style={{
            ...styles.bottomRight,
            fontSize: footerFontSize,
            gap: compact ? 7 : 10,
          }}
        >
          <span style={styles.navBtn}>|◁</span>
          <span style={styles.navBtn}>◁</span>
          <span
            style={{
              ...styles.pageDot,
              width: compact ? 17 : 20,
              height: compact ? 17 : 20,
              fontSize: compact ? 10.5 : 12,
            }}
          >
            1
          </span>
          <span style={styles.navMuted}>próximo ▷</span>
          <span style={styles.navBtn}>▷|</span>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    width: "100%",
    border: "1px solid #d5d9df",
    borderRadius: 0,
    overflow: "hidden",
    background: "#fff",
  },

  scroller: {
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    background: "#fff",
  },

  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    minWidth: "max-content",
  },

  th: {
    textAlign: "left",
    fontWeight: 700,
    borderRight: "1px solid",
    borderBottom: "1px solid",
    padding: 0,
    whiteSpace: "nowrap",
    userSelect: "none",
    verticalAlign: "middle",
  },

  thInner: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%",
  },

  thText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  filterCell: {
    borderRight: "1px solid",
    borderBottom: "1px solid",
  },

  filterInput: {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    fontFamily: "'Open Sans', sans-serif",
    textAlign: "center",
    color: "#7a8190",
    fontWeight: 600,
    textTransform: "uppercase",
    boxSizing: "border-box",
  },

  td: {
    borderRight: "1px solid",
    borderBottom: "1px solid",
    fontWeight: 400,
    textAlign: "left",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    boxSizing: "border-box",
  },

  cellInner: {
    display: "block",
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#000000",
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 500,
  },

  qtyWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },

  qtyBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderRadius: 2,
    cursor: "pointer",
    fontWeight: 700,
    lineHeight: 1,
    padding: 0,
    fontFamily: "'Open Sans', sans-serif",
    boxSizing: "border-box",
  },

  qtyValue: {
    textAlign: "center",
    color: "#000000",
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 700,
  },

  emptyCell: {
    textAlign: "center",
    color: "#7a8190",
    borderBottom: "1px solid",
    background: "#fff",
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 600,
  },

  bottomBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontFamily: "'Open Sans', sans-serif",
  },

  bottomLeft: {
    color: "#6f7683",
    fontWeight: 600,
  },

  bottomRight: {
    display: "flex",
    alignItems: "center",
    color: "#9097a3",
  },

  navBtn: {
    opacity: 0.7,
    cursor: "pointer",
    userSelect: "none",
  },

  navMuted: {
    opacity: 0.7,
    userSelect: "none",
  },

  pageDot: {
    borderRadius: "50%",
    background: "#cadf9b",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
  },
};

export default MovementTable;
